import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import { curry, invert, isEmpty, keyBy, mapValues, without, pick, noop } from 'mojito/utils';

const {
    selectors: favoritesSelectors,
    slice: { actions: favoritesActions },
} = MojitoServices.Favorites;
const { useLongPress } = MojitoPresentation.Hooks;
const CoreHooks = MojitoCore.Presentation.Hooks;
const { useControllerHelper } = MojitoCore.Presentation.Hooks;
const { descriptors: EventsDescriptors } = MojitoServices.SportsContent.Events;
const { selectors: itemListSelectors, descriptor: ItemListDataDescriptor } =
    MojitoServices.ItemList;
const { descriptor: MenusDescriptor, selectors: menusSelectors } =
    MojitoServices.SportsContent.Menus;
const { selectMenuContentInfo, selectMenuState } = menusSelectors;
const {
    selectEventItem,
    selectEventState,
    makeSelectMarketsByIds,
    selectMarketState,
    selectEventTips,
    selectTipsState,
} = MojitoServices.SportsContent.Events.selectors;
const { CONTENT_STATE } = MojitoServices.Common.types;
const ServicesUtils = MojitoServices.Common.utils;
const {
    selectors: { selectMarketGroupState },
} = MojitoServices.SportsContent.MarketGroups;
const {
    createEventsDescriptor,
    createEventMarketsDescriptor,
    createEventsChunkDescriptor,
    createMarketsChunkDescriptor,
    createEventTipsDescriptor,
} = EventsDescriptors;
const {
    descriptor: { createEventGroupDescriptor, createEventGroupsChunkDescriptor },
    selectors: { selectEventGroup, selectEventGroupState, selectEventGroups },
} = MojitoServices.EventGroups;
const { selectItems, selectItemsState } = itemListSelectors;
const {
    descriptor: ContainerDataDescriptor,
    selectors: { selectItems: selectContainerItems },
} = MojitoServices.SportsContent.ContainerItems;
const {
    selectors: sportMetaInformationSelectors,
    descriptor: { createSportMetaInformationDescriptor },
} = MojitoServices.SportsContent.SportMetaInformation;
const { selectSportMetaInformation, selectSportMetaInformationState } =
    sportMetaInformationSelectors;
const {
    descriptor: LottoDescriptor,
    selectors: { selectLottery, selectLotteryState },
} = MojitoServices.Lottery;
const {
    descriptor: MarketGroupsDescriptor,
    selectors: { selectMarketGroups },
} = MojitoServices.SportsContent.MarketGroups;
const { createGroupsDescriptor } = MarketGroupsDescriptor;
const { byDisplayOrder } = MojitoPresentation.Utils.SortingHelper;
const {
    types: { PRIORITY_TYPES },
    selectors: { selectPrioritySport },
} = MojitoServices.UserSettings;

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

/**
 * Contains custom hooks for general module functionality.
 *
 * @class Hooks
 * @memberof Mojito.Modules.Common
 */

/**
 * Hook listens for SportsContent events store slice changes and returns event item object by eventId.
 * On first render hook requests data.
 *
 * @function useEventItem
 *
 * @param {string} eventId - Event Id.
 * @param {boolean} [shouldRequest = true] - Flag indicating if hook should perform event request.
 * @returns {object} The output of {@link Mojito.Services.SportsContent.Events.selectors#selectEventItem|selectEventItem} function.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventItem = (eventId, shouldRequest = true) => {
    useEventRequest(shouldRequest ? eventId : undefined);
    return useSelector(state => selectEventItem(eventId, state));
};

/**
 * Hook listens to the events slice of the Redux store. Returns true if the event item is unavailable or ended, otherwise returns false.
 *
 * @function useEventItemUnavailability
 *
 * @param {string} eventId - Event Id.
 * @returns {boolean} Returns true when the event is unavailable, ended or has deletedTime value, otherwise - false.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventItemUnavailability = eventId => {
    const eventItem = useEventItem(eventId);
    const eventState = useSelector(state => selectEventState(eventId, state));
    return eventState === CONTENT_STATE.UNAVAILABLE || eventItem?.ended || !!eventItem?.deletedTime;
};

/**
 * Hook listens for SportsContent events store slice changes and returns event tips list by eventId.
 * On first render hook requests data.
 *
 * @function useEventTips
 *
 * @param {string} eventId - Event Id.
 * @param {boolean} [shouldRequest = true] - Flag indicating if hook should perform tip request.
 * @returns {object} The output of {@link Mojito.Services.SportsContent.Events.selectors#selectEventTips|selectEventTips} function.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventTips = (eventId, shouldRequest = true) => {
    useEventTipRequest(shouldRequest ? eventId : undefined);
    return useSelector(state => selectEventTips(eventId, state));
};

/**
 * Hook listens for SportsContent events store slice changes and returns market item objects by marketIds.
 * On first render hook requests data.
 *
 * @function useMarketItems
 *
 * @param {Array<string>} marketIds - List of market ids.
 * @param {boolean} [shouldRequest = true] - Flag indicating if hook should perform markets request.
 *
 * @returns {Array<object>} The output of {@link Mojito.Services.SportsContent.Events.selectors#selectMarkets|selectMarkets} function.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketItems = (marketIds, shouldRequest = true) => {
    useMarketsRequest(shouldRequest ? marketIds : undefined);
    const selectMarketsByIds = useMemo(makeSelectMarketsByIds, []);
    return useSelector(state => selectMarketsByIds(marketIds, state));
};

/**
 * Hook performs event data request for specified <code>eventId</code>.
 * The difference between this hook and {@link Mojito.Modules.Common.Hooks.useEventItem|useEventItem} is that this one only performs subscription
 * and doesn't return actual event item from the store.
 *
 * @function useEventRequest
 *
 * @param {string} eventId - Event id.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventRequest = eventId => {
    const eventIds = useMemo(() => (eventId ? [eventId] : undefined), [eventId]);
    useEventsRequest(eventIds);
};

/**
 * Hook performs events data request for specified <code>eventIds</code>.
 * It will result in a set of subscriptions per each event item.
 *
 * @function useEventsRequest
 *
 * @param {Array<string>} eventIds - Event ids.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventsRequest = eventIds => {
    const descriptors = useMemo(() => {
        return eventIds ? [createEventsDescriptor(eventIds)] : EMPTY_ARRAY;
    }, [eventIds]);
    useRequest(descriptors);
};

/**
 * Hook performs event tips data request for specified <code>eventId</code>.
 * The difference between this hook and {@link Mojito.Modules.Common.Hooks.useEventTips|useEventTips} is that this one only performs subscription
 * and doesn't return actual event tips item from the store.
 *
 * @function useEventTipRequest
 *
 * @param {string} eventId - Event id.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventTipRequest = eventId => {
    const eventIds = useMemo(() => (eventId ? [eventId] : undefined), [eventId]);
    useEventTipsRequest(eventIds);
};

/**
 * Hook performs event tips data request for specified <code>eventIds</code>.
 * It will result in a set of subscriptions per each event tip item.
 *
 * @function useEventTipsRequest
 *
 * @param {Array<string>} eventIds - Event ids.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventTipsRequest = eventIds => {
    const descriptors = useMemo(() => {
        return eventIds ? [createEventTipsDescriptor(eventIds)] : EMPTY_ARRAY;
    }, [eventIds]);
    useRequest(descriptors);
};

/**
 * Hook performs markets data request for specified <code>eventId</code> and <code>marketIds</code>.
 *
 * @function useMarketsRequest
 *
 * @param {Array} [marketIds = []] - The list of market ids to request data for.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketsRequest = (marketIds = []) => {
    const descriptor = useMemo(() => {
        const ids = marketIds.filter(Boolean);
        return ids.length ? createEventMarketsDescriptor(ids) : undefined;
    }, [marketIds]);
    useRequest([descriptor]);
};

/**
 * Hook performs events data request for specified <code>eventIds</code>.
 * It will result in a single multi subscription to load events in one data chunk.
 *
 * @function useEventsChunkRequest
 *
 * @param {Array<string>} eventIds - Event ids.
 * @param {string} [instanceId] - Component instance id.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventsChunkRequest = (eventIds, instanceId) => {
    const descriptors = useMemo(() => {
        return eventIds?.length ? [createEventsChunkDescriptor(eventIds, instanceId)] : EMPTY_ARRAY;
    }, [eventIds, instanceId]);
    useRequest(descriptors, instanceId);
};

/**
 * Hook fetches market groups from content.
 *
 * @function useMarketGroups
 *
 * @param {Array<string>} [marketGroupIds] - The list of market group ids.
 * @param {string} [instanceId] - Subscriber instance id.
 * @param {boolean} [shouldRequest = true] - Flag indicating if hook should perform market groups request.
 *
 * @returns {object} Market groups object. Keys are market group ids, values are actual group objects.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketGroups = (marketGroupIds, instanceId, shouldRequest = true) => {
    const descriptors = useMemo(
        () =>
            marketGroupIds && shouldRequest
                ? [createGroupsDescriptor(marketGroupIds, instanceId)]
                : EMPTY_ARRAY,
        [marketGroupIds, shouldRequest, instanceId]
    );
    useRequest(descriptors);
    return useSelector(
        state =>
            marketGroupIds?.length
                ? pick(selectMarketGroups(state), ...marketGroupIds)
                : EMPTY_OBJECT,
        shallowEqual
    );
};

/**
 * Hook performs markets data request for specified <code>marketIds</code>.
 * It will result in a single multi subscription to load markets in one data chunk.
 *
 * @function useMarketsChunkRequest
 *
 * @param {Array<string>} marketIds - Market ids.
 * @param {string} [instanceId] - Component instance id.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketsChunkRequest = (marketIds, instanceId) => {
    const descriptors = useMemo(() => {
        return marketIds?.length
            ? [createMarketsChunkDescriptor(marketIds, instanceId)]
            : EMPTY_ARRAY;
    }, [marketIds, instanceId]);
    useRequest(descriptors, instanceId);
};

/**
 * Hook performs lotteries data request for specified <code>lotteryIds</code>.
 * It will result in a single multi subscription to load lottery content items in one data chunk.
 *
 * @function useLotteriesChunkRequest
 *
 * @param {Array<string>} [lotteryIds] - Lottery ids.
 * @param {string} [instanceId] - Component instance id.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useLotteriesChunkRequest = (lotteryIds, instanceId) => {
    const descriptors = useMemo(() => {
        return lotteryIds?.length
            ? [LottoDescriptor.createLotteriesChunkDescriptor(lotteryIds, instanceId)]
            : EMPTY_ARRAY;
    }, [lotteryIds, instanceId]);
    useRequest(descriptors, instanceId);
};

/**
 * Hook performs data request for specified <code>eventId</code> and <code>marketId</code>.
 * Difference between this hook and {@link Mojito.Modules.Common.Hooks.useMarketsRequest|useMarketsRequest} is that this one requests single market.
 *
 * @function useMarketRequest
 *
 * @param {string} marketId - The id of the market to request data for.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketRequest = marketId => {
    const marketIds = useMemo(() => [marketId], [marketId]);
    useMarketsRequest(marketIds);
};

/**
 * Hook performs data request for items list with specified <code>listId</code>.
 * It will use {@link Mojito.Services.ItemList.store#selectItems|selectItems} function of item list store to fetch the data.
 * If data is not in the store it will be requested from backend.
 *
 * @function useListItems
 *
 * @param {string} listId - The id of the list to request data for.
 * @returns {Array} List of items. The output of {@link Mojito.Services.ItemList.store#selectItems|selectItems} function.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useListItems = listId => {
    const descriptor = useMemo(() => listId && ItemListDataDescriptor.create(listId), [listId]);

    useRequest([descriptor]);
    return useSelector(state => selectItems(listId, state));
};

/**
 * Hook performs data request for each event group id from <code>groupIds</code> list and returns event groups from state.
 *
 * @function useEventGroup
 *
 * @param {string} groupId - The list of desired event group ids.
 * @param {boolean} [shouldRequest = true] - Flag indicating if hook should perform event groups request.
 *
 * @returns {object} Event group object from state.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventGroup = (groupId, shouldRequest = true) => {
    const descriptors = useMemo(() => {
        return groupId && shouldRequest ? [createEventGroupDescriptor(groupId)] : EMPTY_ARRAY;
    }, [groupId, shouldRequest]);
    useRequest(descriptors);
    return useSelector(state => selectEventGroup(groupId, state));
};

/**
 * Hook performs data request for each event group id from <code>groupIds</code> list and returns event groups from state.
 * It will result in a single multi subscription to load all groups in a one data chunk.
 *
 * @function useEventGroups
 *
 * @param {Array<string>} [groupIds] - The list of desired event group ids.
 * @param {string} [instanceId] - Component instance id.
 *
 * @returns {object} Object with event groups selected by groupIds from state. The key is group id and value is actual group item.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventGroups = (groupIds, instanceId) => {
    const descriptors = useMemo(() => {
        return groupIds?.length
            ? [createEventGroupsChunkDescriptor(groupIds, instanceId)]
            : EMPTY_ARRAY;
    }, [groupIds, instanceId]);
    useRequest(descriptors);
    return useSelector(
        state => (groupIds?.length ? pick(selectEventGroups(state), ...groupIds) : EMPTY_OBJECT),
        shallowEqual
    );
};

/**
 * Hook performs data request for sport meta information document. Will be used to render top sports coupon.
 *
 * @function useSportMetaInfo
 *
 * @param {string} sportListName - Sport list name to request.
 *
 * @returns {Array} Sport meta information list.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useSportMetaInfo = sportListName => {
    const descriptors = useMemo(
        () => (sportListName ? [createSportMetaInformationDescriptor(sportListName)] : EMPTY_ARRAY),
        [sportListName]
    );
    useRequest(descriptors);
    return useSelector(state => selectSportMetaInformation(sportListName, state));
};

/**
 * Hook returns true once content items loading has been finalized
 * (the state of all items is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that the items have been requested, and we got response from a server with or without content data for each of it.
 *
 * @function useItemsLoadDone
 *
 * @param {Function} itemStateSelector - Content item selector function.
 * @param {Array} itemIds - Item ids list.
 *
 * @returns {boolean} True if all items with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useItemsLoadDone = (itemStateSelector, itemIds) => {
    return useSelector(state => {
        const loadingStates = (itemIds || [])
            .filter(Boolean)
            .map(itemId => itemStateSelector(itemId, state));
        return !isEmpty(loadingStates) && loadingStates.every(ServicesUtils.isContentLoadDone);
    });
};

/**
 * Hook returns true once items list loading has been finalized
 * (the state of all items is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that the item lists have been requested and we got response from a server with or without data for each of it.
 *
 * @function useItemListLoadDone
 *
 * @param {Array} listIds - Item list ids.
 *
 * @returns {boolean} True if all item lists with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useItemListLoadDone = curry(useItemsLoadDone)(selectItemsState);

/**
 * Hook returns true once events loading has been finalized
 * (the state of all events is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that the events have been requested and we got response from a server with or without event data for each of it.
 *
 * @function useEventsLoadDone
 *
 * @param {Array} eventIds - Event ids.
 *
 * @returns {boolean} True if all events with provided ids have finalised their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventsLoadDone = curry(useItemsLoadDone)(selectEventState);

/**
 * Hook returns true once market groups loading has been finalized
 * (the state of all groups is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that the groups have been requested and we got response from a server with or without group data for each of it.
 *
 * @function useMarketGroupsLoadDone
 *
 * @param {Array} marketGroupIds - The id of the market group.
 *
 * @returns {boolean} True if all market groups with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketGroupsLoadDone = curry(useItemsLoadDone)(selectMarketGroupState);

/**
 * Hook returns true once markets loading has been finalized
 * (the state of all markets is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that markets have been requested and we got response from a server with or without market data for each of it.
 *
 * @function useMarketsLoadDone
 *
 * @param {Array} marketIds - The list of market ids.
 *
 * @returns {boolean} True if all markets with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketsLoadDone = curry(useItemsLoadDone)(selectMarketState);

/**
 * Hook returns true once markets loading has been finalized
 * (the state of all markets is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that markets have been requested and we got response from a server with or without market data for each of it.
 *
 * @function useSportMetasLoadDone
 *
 * @param {Array} marketIds - The list of market ids.
 *
 * @returns {boolean} True if all markets with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useSportMetasLoadDone = curry(useItemsLoadDone)(selectSportMetaInformationState);

/**
 * Hook returns true once event groups loading has been finalized
 * (the state of all markets is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that groups have been requested and we got response from a server with or without group data for each of it.
 *
 * @function useEventGroupsLoadDone
 *
 * @param {Array} eventGroupIds - The list of event group ids.
 *
 * @returns {boolean} True if all groups with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventGroupsLoadDone = curry(useItemsLoadDone)(selectEventGroupState);

/**
 * Hook returns true once event tips loading has been finalized
 * (the state of all tips is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that event tips have been requested and we got response from a server with or without tip data for each of it.
 *
 * @function useEventTipsLoadDone
 *
 * @param {Array} eventIds - The list of event ids.
 *
 * @returns {boolean} True if all event tips with provided event ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useEventTipsLoadDone = curry(useItemsLoadDone)(selectTipsState);

/**
 * Hook performs data request for container items by <code>containerId</code>.
 * If data is not in the store it will be requested from backend.
 *
 * @function useContainerItems
 *
 * @param {string} containerId - Desired container id.
 * @returns {Array} List of container items.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useContainerItems = containerId => {
    const descriptor = useMemo(
        () => containerId && ContainerDataDescriptor.create(containerId),
        [containerId]
    );
    useRequest([descriptor]);
    return useSelector(state => selectContainerItems(containerId, state));
};

/**
 * Hook performs data request using specified <code>descriptors</code>. The request will be performed in a loop for each descriptor in the list.
 * Every time when descriptors change, the hook will re-requests data.
 *
 * @function useRequest
 *
 * @param {Array<object>} descriptors - List of descriptor objects for correct data request. See {@link Mojito.Core.Services.RequestDataHelper#requestData|requestData} function.
 * @param {string} [instanceId] - Component instance id.
 *
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useRequest = (descriptors, instanceId) => {
    const requestHelper = useControllerHelper(instanceId);
    useEffect(() => {
        descriptors.filter(Boolean).forEach(requestHelper.requestData.bind(requestHelper));
    }, [descriptors, requestHelper]);
};

/**
 * @typedef InPlaySportsData
 * @type {object}
 * @property {boolean} loadDone - Loading status.
 * @property {object} eventGroups  - The map of event groups per sport. The key is a sport id, value is an array of event groups which belongs to particular sport.
 * @property {Array<string>} sportIds - List of InPlay sports ids.
 * @memberof Mojito.Modules.Common.Hooks
 */

/**
 * The hook will load sport ids that are currently in-play for provided sportListName and resolve events group id for each of the loaded sport in order to load live events per sport.
 * The event group ids are generated using -live-and-upcoming-match-and-outright-events prefix.
 * Example:if sportListName is set to "sports-with-live-and-upcoming-events" then we will load sport ids list from sports-with-live-and-upcoming-events list item.
 * E.g., we got [soccer, tennis] sports. This means that those sports have live events.
 * Then we will load event groups for each sport using prefix which will lead to requests to soccer-live-and-upcoming-match-and-outright-events and tennis-live-and-upcoming-match-and-outright-events.
 * The result will contain the map of event groups per sport, the list of live sport ids and isPending flag useful for loading animations.
 *
 * @param {string} sportListName - Name of list containing in-play sports.
 * @param {string} instanceId - The id of the client subscriber.
 * @returns {Mojito.Modules.Common.Hooks.InPlaySportsData} InPlay sports data.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useInPlaySportsData = (sportListName, instanceId) => {
    if (!sportListName) {
        throw new Error('sportListName param must be passed to the useInplayData hook');
    }

    const { subscriptionResolver } = CoreHooks.useAppContext();

    const sportIds = useListItems(sportListName);
    const sportListLoadDone = useItemListLoadDone([sportListName]);
    const noInPlaySports = sportListLoadDone && isEmpty(sportIds);

    const sportGroupIds = useMemo(() => {
        return sportIds?.map(sportId =>
            subscriptionResolver.liveAndUpcomingMatchAndOutrightEvents(sportId)
        );
    }, [sportIds, subscriptionResolver]);

    const eventGroups = useEventGroups(sportGroupIds, instanceId);
    const eventGroupsLoadDone = useEventGroupsLoadDone(sportGroupIds);
    const noEventGroups = eventGroupsLoadDone && isEmpty(eventGroups);
    const loadDone = noInPlaySports || noEventGroups || (sportListLoadDone && eventGroupsLoadDone);

    const eventGroupsPerSport = useMemo(() => {
        const sportIdToGroupIdMap = invert(
            keyBy(sportIds, sportId =>
                subscriptionResolver.liveAndUpcomingMatchAndOutrightEvents(sportId)
            )
        );
        return mapValues(sportIdToGroupIdMap, groupId => eventGroups[groupId]);
    }, [sportIds, eventGroups, subscriptionResolver]);

    return { loadDone, eventGroups: eventGroupsPerSport, sportIds };
};

/**
 * Hook listens for SportsContent menus store changes and returns sport menu content based on time zone and sport id.
 * On first render hook requests data.
 *
 * @function useMenuContent
 *
 * @param {string} sportId - Sport id.
 * @param {string} timeZone - Time zone.
 * @returns {Mojito.Services.SportsContent.Menus.types.MenuConent} Menu content info.
 * @memberof Mojito.Modules.Common.Hooks
 */

export const useMenuContent = (sportId, timeZone) => {
    const descriptors = useMemo(() => [MenusDescriptor.create(timeZone)], [timeZone]);
    useRequest(descriptors);

    return useSelector(state => selectMenuContentInfo(sportId, timeZone, state));
};

/**
 * Hook listens to the menus slice of the Redux store. Returns true if the menu is loading, otherwise returns false.
 *
 * @function useMenuLoading
 *
 * @param {string} timeZone - Time zone.
 * @returns {boolean} Returns true when the menu is loading, otherwise - false.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMenuLoading = timeZone => {
    const menuState = useSelector(state => selectMenuState(timeZone, state));
    const { UNKNOWN, PENDING } = CONTENT_STATE;

    return menuState === UNKNOWN || menuState === PENDING;
};

/**
 * Hook performs data request for lottery by <code>id</code>.
 * If data is not in the store it will be requested from backend.
 *
 * @function useLottery
 *
 * @param {string} id - Lottery id.
 * @param {boolean} [shouldRequest = true] - Flag indicating if hook should perform lottery request.
 * @returns {object|undefined} Lottery if it exists, else undefined.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useLottery = (id, shouldRequest = true) => {
    const descriptors = useMemo(
        () => (id && shouldRequest ? [LottoDescriptor.createLotteryDescriptor(id)] : EMPTY_ARRAY),
        [id, shouldRequest]
    );
    useRequest(descriptors);
    return useSelector(state => selectLottery(id, state));
};

/**
 * Hook returns true once lotteries loading has been finalized
 * (the state of all lotteries is either {@link Mojito.Services.Common.types.CONTENT_STATE|AVAILABLE} or {@link Mojito.Services.Common.types.CONTENT_STATE|UNAVAILABLE})
 * which means that lotteries have been requested and we got response from a server with or without lotteries data for each of it.
 *
 * @function useLotteriesLoadDone
 *
 * @param {Array} lotteryIds - The list of lottery ids.
 *
 * @returns {boolean} True if all lotteries with provided ids have finalized their loading.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useLotteriesLoadDone = curry(useItemsLoadDone)(selectLotteryState);

/**
 * Hook that sorts and returns items based on priority sorting type and priority sport.
 * Item should have sport id and displayOrder - display order of an item.
 *
 * @function useSortedItems
 *
 * @param {Array} items - The list of items.
 * @param {Mojito.Services.UserSettings.types.PRIORITY_TYPES} priorityType - Priority to be used for sorting.
 * @param {Function} [getSportId] - A function to retrieve a sport id. Default value is <code>obj => obj.id</code>.
 *
 * @returns {Array} The items in sorted order.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useSortedItems = (items, priorityType, getSportId = obj => obj.id) => {
    const prioritySportId = useSelector(state => selectPrioritySport(state));
    return useMemo(() => {
        const sortedItems = items ? [...items].sort(byDisplayOrder) : [];
        if (sortedItems.length === 0 || !priorityType || !prioritySportId) {
            return sortedItems;
        }
        const topDisplayOrder = sortedItems[0].displayOrder;
        const shouldPersistTopDisplayOrder = priorityType === PRIORITY_TYPES.SECOND;
        const firstChunk = shouldPersistTopDisplayOrder
            ? items.filter(item => item.displayOrder === topDisplayOrder)
            : [];
        const secondChunk = without(items, ...firstChunk)
            .filter(item => getSportId(item) === prioritySportId)
            .sort(byDisplayOrder);
        const thirdChunk = without(items, ...[...firstChunk, ...secondChunk]).sort(byDisplayOrder);

        return [...firstChunk, ...secondChunk, ...thirdChunk];
    }, [items, priorityType, prioritySportId, getSportId]);
};

/**
 * Hook extracts a flat array of market IDs from an array of market info objects.
 *
 * @function useMarketIdsFromMarketInfos
 *
 * @param {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>} marketInfos - Array of market info objects.
 *
 * @returns {Array<string>} Array of market IDs.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useMarketIdsFromMarketInfos = marketInfos => {
    return useMemo(
        () =>
            marketInfos
                .flatMap(info => info.markets)
                .map(market => market?.id)
                .filter(Boolean),
        [marketInfos]
    );
};

/**
 * Hook that returns content items by ids for specified selector.
 *
 * @function useContentItems
 *
 * @param {Function} selector - Content item selector function.
 * @param {Array<string>} itemIds - Content item ids list to request.
 *
 * @returns {Array} Content items.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useContentItems = (selector, itemIds) => {
    const itemsInfo = useSelector(
        state => (itemIds ? pick(selector(state), ...itemIds) : undefined),
        shallowEqual
    );
    return useMemo(() => (itemsInfo ? Object.values(itemsInfo) : undefined), [itemsInfo]);
};

/**
 * Hook that manages favorite event state and provides handlers to toggle favorite status.
 *
 * @function useFavoriteEvent
 *
 * @param {object} event - Event object.
 * @param {object} config - Configuration object.
 * @param {{active: object, inactive: object}} configStates - Configuration for active and inactive states.
 * @param {Function} [emitAnalytics] - Analytics emitter function.
 *
 * @returns {{isFavoritingEnabled: boolean, isLongPressEnabled: boolean, isFavoriteEvent: boolean, toggleFavorites: Function, longPressRef: object, currentConfig: object}} Favorite event state and handlers.
 * @memberof Mojito.Modules.Common.Hooks
 */
export const useFavoriteEvent = (event, config, configStates, emitAnalytics = noop) => {
    const dispatch = useDispatch();
    const favorites = useSelector(favoritesSelectors.selectFavoriteEvents);
    const eventId = event?.id;
    const [currentConfig, setCurrentConfig] = useState(configStates.inactive);
    const ref = useRef(null);

    const isFavoritingEnabled = event?.details.inPlay && config.enableFavoriting;
    const isLongPressEnabled = isFavoritingEnabled && config.enableLongPress;
    const isFavoriteEvent = isFavoritingEnabled && favorites.includes(eventId);

    const toggleFavorites = useCallback(() => {
        const element = ref.current;
        if (!element) return;

        setCurrentConfig(configStates.active);

        const handleEnd = () => {
            setCurrentConfig(configStates.inactive);
            element.removeEventListener('transitionend', handleEnd);
        };

        element.addEventListener('transitionend', handleEnd);

        if (isFavoriteEvent) {
            emitAnalytics('removeEventFromFavorites', { eventId });
            dispatch(favoritesActions.removeFavoriteEventId(eventId));
        } else {
            emitAnalytics('addEventToFavorites', { eventId });
            dispatch(favoritesActions.addFavoriteEventId(eventId));
        }
    }, [isFavoriteEvent, dispatch, eventId, emitAnalytics, configStates]);

    const longPressRef = useLongPress(ref, toggleFavorites, config.longPressDuration);
    return {
        isFavoritingEnabled,
        isLongPressEnabled,
        isFavoriteEvent,
        toggleFavorites,
        longPressRef,
        currentConfig,
    };
};
